import { Injectable, EventEmitter } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { map, tap, mapTo, catchError } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Helpers } from "./app.helpers";
import { IProfileDTO } from "../core/models/profile.dto";

export interface Credentials {
    username: string;
    password: string;
    grant_type: string;
    client_id: string;
    client_secret: string;
}

export interface RefreshTokenRequestModel {
    token: string;
}

export interface LoginSuccessModel {
    userName: string;
    userId: string;
    access_token: string;
    refresh_token: string;
    ".expires": string;
}


@Injectable()
export class AuthenticationService {
    public loginSuccess: EventEmitter<LoginSuccessModel> = new EventEmitter<LoginSuccessModel>();
    public logoutSuccess: EventEmitter<any> = new EventEmitter<any>();
    public profileUpdated: EventEmitter<any> = new EventEmitter<any>();

    private userStorageKey = "user";
    private profileStoraggeKey = "_profile";
    private jwtTokenKey = "JWT_TOKEN";
    private refreshTokenKey = "REFRESH_TOKEN";
    private readonly clientId = "1_4ttllud90bok08k48w4k000s4wsko4wcg8soskw48kgo88wokc";
    private readonly clientSecret = "5pf9lu8dhlwk4w4kkgg4c8cggw4ogwwco8sg0wo4swgc0cow8g";
    private readonly loginGrantType = "password";
    private readonly refreshTokenGrantType = "refresh_token";
    private loggedUser: string;

    constructor(private http: HttpClient) { }

    public login(model: Credentials): Observable<boolean> {
        model.username = (model as any).email;
        return this.http
            .post<LoginSuccessModel>(`${environment.authenticationServer}/auth/token`, model, { headers: { "Content-Type": "application/json" } })
            .pipe(
                tap(x => this.doLoginUser(model.username, x)),
                mapTo(true),
                catchError(error => {
                    return of(false);
                })
            );
    }

    public register(model) {
        return this.http.post(`${environment.serverPath}/auth/register`, model);
    }

    checkEmailExists(email: string) {
        return this.http.post(`${environment.serverPath}/auth/checkEmail`, { email });
    }

    public getUserProfile() {
        return this.http.get<IProfileDTO>(`${environment.serverPath}/profile`);
    }

    public saveLocalProfile(profile: IProfileDTO) {
        localStorage.setItem(this.profileStoraggeKey, JSON.stringify(profile));
        this.profileUpdated.emit();
    }

    public getLocalProfile() {
        const p = localStorage.getItem(this.profileStoraggeKey);

        if (!p) return null;

        return JSON.parse(p) as IProfileDTO;
    }

    public logout() {
        this.doLogoutUser();
        return of(true);
    }

    public refreshToken(): Observable<LoginSuccessModel> {
        const model: RefreshTokenRequestModel = {
            token: this.getRefreshToken()
        };

        return this.http.post<LoginSuccessModel>(`${environment.authenticationServer}/auth/refresh-token`, model)
            .pipe(
                tap(x => {
                    this.storeTokens(x);
                })
            );

    }

    public getTokenModel() {
        if (localStorage.getItem(this.userStorageKey)) {
            try {
                return JSON.parse(localStorage.getItem(this.userStorageKey));
            } catch (err) { return undefined; }
        }
        return undefined;
    }

    public getUserId() {
        if (this.isUserAuthenticated()) {
            return JSON.parse(localStorage.getItem(this.userStorageKey)).userId;
        }

        return undefined;
    }

    public getJwtToken() {
        return localStorage.getItem(this.jwtTokenKey);
    }

    public isUserAuthenticated(): boolean {
        const tokenReponse = this.getTokenModel();

        if (!tokenReponse) return false;

        if (new Date(tokenReponse[".expires"]) < new Date()) return false;

        return true;
    }

    public hasRole(role: string) {
        if (this.getLocalProfile().roles.includes(role)) {
            return true;
        }

        return false;
    }

    private getRefreshToken() {
        return localStorage.getItem(this.refreshTokenKey);
    }

    public doLoginUser(username: string, loginResponse: LoginSuccessModel) {
        this.loggedUser = username;
        this.storeTokens(loginResponse);
        this.loginSuccess.emit(loginResponse);
    }

    private doLogoutUser() {
        let activeToken;
        if (this.getJwtToken()) {
            activeToken = this.getJwtToken().toString();
        }

        this.loggedUser = null;
        this.removeTokens();

        localStorage.removeItem("gymSettings");
        localStorage.removeItem("language");
        localStorage.removeItem("_profile");

        if (activeToken) {
            this.logoutSuccess.emit(activeToken);
        }
    }

    private removeTokens() {
        localStorage.removeItem(this.jwtTokenKey);
        localStorage.removeItem(this.refreshTokenKey);
        localStorage.removeItem(this.userStorageKey);
    }

    private storeTokens(loginResponse: LoginSuccessModel) {
        localStorage.setItem(this.jwtTokenKey, loginResponse.access_token);
        localStorage.setItem(this.refreshTokenKey, loginResponse.refresh_token);
        localStorage.setItem(this.userStorageKey, JSON.stringify(loginResponse));
    }

    async authorize() {
        try {
            const response = await this.http.get(`${environment.serverPath}/auth/authorize`).toPromise();
            return response;
        } catch (err) {
            console.log(err);
            return false;
        }
    }
}